import { useReducedMotion } from "framer-motion";

/**
 * Critically damped, no overshoot: the rail arrives and swaps between states, it
 * is never a momentum gesture, so bounce would read as decoration. 0.36s sits in
 * the drawer/panel band.
 */
export const RAIL_SPRING = { type: "spring", bounce: 0, duration: 0.36 } as const;

/** Reduced motion keeps the cross-fade and drops the travel — gentler, not absent. */
const REDUCED_TRANSITION = { duration: 0.15 } as const;

/**
 * The rail's single motion signature, shared by its arrival on the page and by
 * its internal swaps — previously the same two constants were retyped in both
 * files and had already started to drift.
 *
 * The transform is one string rather than framer's `x` / `scale` shorthands:
 * independent transform values are composed in JS every frame, while a single
 * `transform` can be handed to the compositor. It matters here because the rail
 * animates over a dense table.
 */
export function useRailMotion({ travel, scale = 1 }: { travel: number; scale?: number }) {
  const reduceMotion = useReducedMotion();

  return {
    hidden: {
      opacity: 0,
      transform: reduceMotion ? "translateX(0px) scale(1)" : `translateX(${travel}px) scale(${scale})`,
    },
    visible: { opacity: 1, transform: "translateX(0px) scale(1)" },
    transition: reduceMotion ? REDUCED_TRANSITION : RAIL_SPRING,
  };
}

/**
 * Content swapping inside the panel — skeleton to data, one affaire to the next.
 * A tween, not the spring: nothing is travelling, and 0.16s keeps it under the
 * button-feedback ceiling so a fast walk down the rows never queues up.
 *
 * `ease-out` because the content is entering and leaving; `ease-in` on UI would
 * hold the frame back at the moment attention lands on it.
 */
export const PANEL_FADE = { duration: 0.16, ease: [0.23, 1, 0.32, 1] } as const;
